import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { ArrowRight, Mail, Wrench, BarChart3, Compass } from "lucide-react";

const traitChips = [
  "Curious to a fault",
  "Learns by building",
  "Trusts data over simulation",
];

const fade = {
  hidden: { opacity: 0, y: 20 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.5, ease: "easeOut" },
  }),
};

const pillars = [
  {
    icon: Wrench,
    title: "I learn with my hands",
    description:
      "Give me a bench, some hardware, and a problem, and I'll figure it out by making something real. Reading about it only gets me so far.",
  },
  {
    icon: BarChart3,
    title: "I like the messy middle",
    description:
      "Noisy data, weird edge cases, the gap between what should happen and what actually does, that's the part I find interesting.",
  },
  {
    icon: Compass,
    title: "I chase the hard edges",
    description:
      "I gravitate toward places where the usual rules break down. That's where the questions worth answering tend to live.",
  },
];

export default function About() {
  return (
    <div className="flex flex-col">
      <Helmet>
        <title>Soham Desai | Building with care</title>
        <meta name="description" content="Soham Desai's corner of the internet, notes, tools, and experiments from building wireless systems for extreme environments: underwater, remote, and in space." />
        <meta property="og:title" content="Soham Desai | Building with care" />
        <meta property="og:description" content="Notes, tools, and experiments from building wireless systems for extreme environments, underwater, remote, and in space." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://soham-desai.vercel.app/" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Soham Desai | Building with care" />
        <meta name="twitter:description" content="Notes, tools, and experiments from building wireless systems for extreme environments." />
      </Helmet>

      {/* ── Hero: photo + intro split ── */}
      <section className="relative overflow-hidden px-4 md:px-8 pt-16 pb-14 md:pt-24 md:pb-20">
        {/* Ambient glow background (no external image) */}
        <div className="pointer-events-none absolute inset-0 -z-10" aria-hidden="true">
          <div className="absolute -top-32 left-1/2 -translate-x-1/2 h-[30rem] w-[30rem] rounded-full bg-accent/10 blur-3xl" />
          <div className="absolute inset-0 bg-gradient-to-b from-surface/30 via-transparent to-transparent" />
        </div>

        <div className="relative max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-[auto,1fr] items-center gap-10 md:gap-16">
          {/* Photo */}
          <motion.div
            variants={fade}
            initial="hidden"
            animate="visible"
            custom={0}
            className="mx-auto md:mx-0"
          >
            <div className="relative">
              <div
                className="absolute -inset-4 rounded-3xl bg-accent/15 blur-2xl"
                aria-hidden="true"
              />
              <img
                src="/profile.jpg"
                alt="Soham Desai"
                className="relative w-48 h-48 sm:w-56 sm:h-56 md:w-64 md:h-64 lg:w-72 lg:h-72 rounded-2xl object-cover object-center border border-border shadow-2xl"
              />
            </div>
          </motion.div>

          {/* Intro */}
          <div className="text-center md:text-left">
            <motion.h1
              variants={fade}
              initial="hidden"
              animate="visible"
              custom={1}
              className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold text-foreground tracking-tight leading-tight"
            >
              Hey, I&rsquo;m Soham.
            </motion.h1>

            <motion.p
              variants={fade}
              initial="hidden"
              animate="visible"
              custom={2}
              className="mt-5 text-lg md:text-xl text-muted leading-relaxed max-w-xl mx-auto md:mx-0"
            >
              I build things that stay{" "}
              <span className="text-accent font-medium">
                connected where they shouldn&rsquo;t be
              </span>
              , underwater, off the grid, and out in space. Mostly, I just
              can&rsquo;t leave a hard problem alone.
            </motion.p>

            {/* Trait chips */}
            <motion.div
              variants={fade}
              initial="hidden"
              animate="visible"
              custom={2.5}
              className="mt-6 flex flex-wrap gap-2.5 justify-center md:justify-start"
            >
              {traitChips.map((tag) => (
                <span
                  key={tag}
                  className="px-3.5 py-1.5 rounded-full border border-border bg-surface/40 text-xs md:text-sm text-muted-foreground"
                >
                  {tag}
                </span>
              ))}
            </motion.div>

            {/* CTAs */}
            <motion.div
              variants={fade}
              initial="hidden"
              animate="visible"
              custom={3}
              className="mt-8 flex flex-wrap items-center gap-3 justify-center md:justify-start"
            >
              <Link
                to="/projects"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-accent text-background text-sm font-medium hover:bg-accent-hover transition-colors"
              >
                Explore my projects
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md border border-border text-foreground text-sm font-medium hover:border-accent/40 hover:text-accent transition-colors"
              >
                <Mail className="w-4 h-4" />
                Get in touch
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── Notebook line ── */}
      <section className="px-4 md:px-8 pb-12 md:pb-14 border-b border-border/60">
        <motion.p
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fade}
          className="max-w-2xl mx-auto text-center text-base md:text-lg text-muted leading-relaxed"
        >
          This site is my notebook in the open, the systems I&rsquo;m building,
          the tools I reach for, and what I learn making communication work where
          it usually fails. Some projects, some photos, some experiments.
        </motion.p>
      </section>

      {/* ── How I work (cards) ── */}
      <section className="px-4 md:px-8 py-14 md:py-16">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fade}
            className="font-display text-2xl md:text-3xl font-semibold text-foreground text-center mb-10"
          >
            How I work
          </motion.h2>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-60px" }}
            variants={{ visible: { transition: { staggerChildren: 0.12 } } }}
            className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8"
          >
            {pillars.map((p) => (
              <motion.div
                key={p.title}
                variants={fade}
                className="group rounded-lg border border-border/60 bg-surface/40 p-6 hover:border-accent/30 transition-colors"
              >
                <div className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-accent/10 mb-4">
                  <p.icon className="w-5 h-5 text-accent" />
                </div>
                <h3 className="font-display text-lg font-semibold text-foreground mb-2">
                  {p.title}
                </h3>
                <p className="text-sm text-muted leading-relaxed">
                  {p.description}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── Short narrative ── */}
      <section className="px-4 md:px-8 pb-16 md:pb-24">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-60px" }}
          variants={{ visible: { transition: { staggerChildren: 0.1 } } }}
          className="max-w-2xl mx-auto"
        >
          <motion.div
            variants={fade}
            className="h-px w-12 bg-accent/60 mx-auto mb-10"
            aria-hidden="true"
          />

          <motion.p
            variants={fade}
            className="text-muted leading-relaxed text-base md:text-lg"
          >
            If I had to sum myself up: I like figuring out how things{" "}
            <span className="text-foreground font-medium">really</span> work, and
            I don&rsquo;t fully trust an answer until I&rsquo;ve built it myself.
            The thread through everything is stubborn, as we push into oceans,
            remote corners, and space, staying connected shouldn&rsquo;t be the
            thing that breaks. I&rsquo;m drawn to the regimes where the usual
            assumptions fall apart: low power, weak signals, and long delays.
          </motion.p>

          <motion.p
            variants={fade}
            className="mt-6 text-muted leading-relaxed text-base md:text-lg"
          >
            That pull first landed me underwater, where I built a working
            multi-antenna link from a blank bench in grad school, which earned a
            Dean&rsquo;s Award and turned into published research. These days I
            chase the same problem at scale at{" "}
            <a
              href="https://www.skylo.tech/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-accent hover:text-accent-hover underline underline-offset-4 decoration-border transition-colors"
            >
              Skylo
            </a>
            , helping build one of the world&rsquo;s largest direct-to-device
            satellite networks. I&rsquo;ve picked up a few patents and papers
            along the way, but what I actually care about is the building itself.
          </motion.p>

          <motion.p
            variants={fade}
            className="mt-8 text-foreground leading-relaxed text-base md:text-lg"
          >
            If any of this resonates, I&rsquo;d love to compare notes,{" "}
            <Link
              to="/contact"
              className="text-accent hover:text-accent-hover underline underline-offset-4 decoration-border transition-colors"
            >
              reach out
            </Link>
            .
          </motion.p>
        </motion.div>
      </section>
    </div>
  );
}
